import React from 'react';
import { useMemo } from 'react';

const inserted = new Set();

const toKebab = value => value.replace(/[A-Z]/g, char => `-${char.toLowerCase()}`);

const hashString = input => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) - hash) + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

const formatValue = value => {
  if (typeof value === 'number') return `${value}px`;
  if (typeof value === 'string') return value.replace(/;$/, '');
  return value;
};

const splitRules = (style = {}) => {
  const base = {};
  const nested = [];

  for (const [key, value] of Object.entries(style || {})) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      nested.push([key, value]);
    } else {
      base[key] = value;
    }
  }

  return { base, nested };
};

const styleToCss = style => Object.entries(style || {})
  .filter(([, value]) => value !== undefined && value !== null && value !== '')
  .map(([key, value]) => `${toKebab(key)}:${formatValue(value)};`)
  .join('');

const buildCss = (selector, style) => {
  const { base, nested } = splitRules(style);
  let css = `${selector}{${styleToCss(base)}}`;

  for (const [key, value] of nested) {
    if (key.startsWith('@media')) {
      css += `${key}{${buildCss(selector, value)}}`;
    } else if (key.startsWith('&')) {
      css += buildCss(key.replace(/&/g, selector), value);
    } else {
      css += buildCss(`${selector} ${key}`, value);
    }
  }

  return css;
};

const injectStyles = (id, styles) => {
  if (typeof document === 'undefined' || inserted.has(id)) return;
  const styleTag = document.createElement('style');
  styleTag.setAttribute('data-climatesens-styles', id);
  styleTag.textContent = styles;
  document.head.appendChild(styleTag);
  inserted.add(id);
};

export const makeStyles = stylesInput => {
  const styleFactory = typeof stylesInput === 'function' ? stylesInput : () => stylesInput;

  return () => useMemo(() => {
    const styles = styleFactory({}) || {};
    const id = hashString(JSON.stringify(styles));
    const classes = {};
    let css = '';

    for (const key of Object.keys(styles)) {
      const className = `cs-${key}-${id}`;
      classes[key] = className;
      css += buildCss(`.${className}`, styles[key]);
    }

    injectStyles(id, css);
    return classes;
  }, []);
};


export const withStyles = stylesInput => Component => props => {
  const useStyles = makeStyles(stylesInput);
  const classes = useStyles();
  return React.createElement(Component, { ...props, classes: { ...(props.classes || {}), ...classes } });
};

export default { makeStyles, withStyles };

